import { useEffect, useRef } from "react";
import { ViewableEntityKind, useRecordRecentlyViewedMutation } from "../../autogen/bff-api";
import { useAppRole } from "../layout/useAppRole";

/**
 * Marks an entity as opened by the current person, feeding the "recently opened" panels on the home.
 *
 * Fires once per entity per mount — StrictMode's double render and every re-render in between are
 * absorbed by the ref, so opening a page is one write, not one per keystroke elsewhere on it.
 *
 * Only buyers record: the panels resolve ids against the buying organization's own read model, so a
 * supplier's view of someone else's competition would be stored and never resolve.
 */
export const useRecordRecentlyViewed = (entityKind: ViewableEntityKind, entityId: string | undefined) => {
  const [record] = useRecordRecentlyViewedMutation();
  const role = useAppRole();
  const recorded = useRef<string>();

  useEffect(() => {
    if (!entityId || role === "supplier") return;
    const key = `${entityKind}:${entityId}`;
    if (recorded.current === key) return;
    recorded.current = key;
    // Bookkeeping the user never asked for: a failure must not surface, but the promise is consumed
    // so a rejection is not left unhandled.
    void record({ recordRecentlyViewedRequest: { entityKind, entityId } })
      .unwrap()
      .catch(() => undefined);
  }, [record, entityKind, entityId, role]);
};
